import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, BarChart3, Users, ShieldOff, ScrollText, LogOut, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { authAPI } from '../../utils/adminAPI';
import { adminUsuariosAPI } from '../../utils/adminUsuariosAPI';
import { useAdminAuth, useAdminAuthRefresh } from './RequireAuth';
import Modal from './Modal';
import EditarUsuarioModal from './EditarUsuarioModal';

const NAV_ITENS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, fim: true },
  { to: '/admin/gerenciar-enquetes', label: 'Gerenciar Enquetes', icon: ClipboardList },
  { to: '/admin/resultados', label: 'Resultados', icon: BarChart3 },
  { to: '/admin/usuarios', label: 'Usuários', icon: Users },
  { to: '/admin/seguranca/ips', label: 'Segurança', icon: ShieldOff, somenteAdmin: true },
  { to: '/admin/seguranca/logs', label: 'Log de auditoria', icon: ScrollText, somenteAdmin: true }
];

const CHAVE_COLAPSADO = 'admin_sidebar_colapsado';

function lerColapsadoSalvo() {
  try {
    return localStorage.getItem(CHAVE_COLAPSADO) === '1';
  } catch (error) {
    return false;
  }
}

function salvarColapsado(valor) {
  try {
    localStorage.setItem(CHAVE_COLAPSADO, valor ? '1' : '0');
  } catch (error) {
    // Sem localStorage disponível (modo privado restrito etc.) — só não persiste
  }
}

// Shell do painel administrativo: sidebar fixa no desktop (>=lg), vira um
// menu gaveta (drawer) com fundo escurecido no mobile — nunca o antigo menu
// horizontal espremido no header. `title` vira o cabeçalho da página atual;
// o conteúdo de cada tela entra em `children`.
const AdminLayout = ({ title, children }) => {
  const navigate = useNavigate();
  const sessao = useAdminAuth();
  const recarregarSessao = useAdminAuthRefresh();
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [colapsado, setColapsado] = useState(lerColapsadoSalvo);
  const [editandoPerfil, setEditandoPerfil] = useState(false);

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/admin/login');
  };

  const alternarColapsado = () => {
    setColapsado((atual) => {
      const novo = !atual;
      salvarColapsado(novo);
      return novo;
    });
  };

  const itensVisiveis = NAV_ITENS.filter((item) => !item.somenteAdmin || sessao?.tipo === 'admin');

  // `desktopColapsado` só se aplica à sidebar fixa — a gaveta do mobile
  // nunca recolhe, sempre abre em largura cheia.
  const conteudoNav = (desktopColapsado = false) => (
    <>
      <div className={`flex items-center gap-2.5 px-5 py-6 ${desktopColapsado ? 'lg:justify-center lg:px-0' : ''}`}>
        <div className="w-9 h-9 rounded-lg bg-[#40A1E6] flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
          PC
        </div>
        <div className={`min-w-0 ${desktopColapsado ? 'lg:hidden' : ''}`}>
          <p className="text-white font-semibold text-sm leading-tight truncate">Painel Administrativo</p>
          <p className="text-primary-300 text-xs truncate">Enquetes - Corumbá</p>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {itensVisiveis.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.fim}
            title={desktopColapsado ? item.label : undefined}
            onClick={() => setDrawerAberto(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                desktopColapsado ? 'lg:justify-center lg:px-0' : ''
              } ${
                isActive
                  ? 'bg-[#40A1E6] text-white shadow-md shadow-[#40A1E6]/30'
                  : 'text-primary-100 hover:bg-white/10 hover:text-white'
              }`
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <span className={desktopColapsado ? 'lg:hidden' : ''}>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-5 pt-3 border-t border-white/10">
        <button
          type="button"
          onClick={() => { setEditandoPerfil(true); setDrawerAberto(false); }}
          title={desktopColapsado ? `${sessao?.nome_completo} — editar perfil` : undefined}
          className={`w-full flex items-center gap-2.5 px-3 py-2 mb-1 rounded-lg hover:bg-white/10 transition-colors text-left ${
            desktopColapsado ? 'lg:justify-center lg:px-0' : ''
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 overflow-hidden">
            {sessao?.tem_foto && sessao?.id ? (
              <img src={adminUsuariosAPI.fotoUrl(sessao.id)} alt="" className="w-full h-full object-cover" />
            ) : (
              sessao?.nome_completo?.[0]?.toUpperCase() || '?'
            )}
          </div>
          <div className={`min-w-0 ${desktopColapsado ? 'lg:hidden' : ''}`}>
            <p className="text-white text-sm font-medium truncate">{sessao?.nome_completo}</p>
            <p className="text-primary-300 text-xs capitalize">{sessao?.tipo}</p>
          </div>
        </button>
        <button
          onClick={handleLogout}
          title={desktopColapsado ? 'Sair' : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-primary-100 hover:bg-white/10 hover:text-white transition-colors ${
            desktopColapsado ? 'lg:justify-center lg:px-0' : ''
          }`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" /> <span className={desktopColapsado ? 'lg:hidden' : ''}>Sair</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-corumba-light flex">
      {/* Sidebar fixa — desktop */}
      <aside
        className={`hidden lg:flex lg:flex-col bg-primary-900 flex-shrink-0 relative transition-all duration-200 ${
          colapsado ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        {conteudoNav(colapsado)}

        <button
          onClick={alternarColapsado}
          aria-label={colapsado ? 'Expandir menu' : 'Recolher menu'}
          className="absolute top-7 -right-3 w-6 h-6 rounded-full bg-[#40A1E6] text-white flex items-center justify-center shadow-md hover:bg-[#3690d1] transition-colors"
        >
          {colapsado ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Gaveta — mobile/tablet. Sempre montada (nunca some do DOM) e
          controlada só por transições CSS (opacity/translate), pra nunca
          competir com uma @keyframes animation na mesma propriedade. Nunca
          recolhe — só a sidebar fixa do desktop tem esse estado. */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          drawerAberto ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerAberto(false)} />
        <aside
          className={`absolute inset-y-0 left-0 flex flex-col w-64 bg-primary-900 shadow-2xl transition-transform duration-300 ${
            drawerAberto ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <button
            onClick={() => setDrawerAberto(false)}
            aria-label="Fechar menu"
            className="absolute top-5 right-3 text-primary-300 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          {conteudoNav(false)}
        </aside>
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Barra superior — mobile */}
        <header className="lg:hidden flex items-center gap-3 bg-primary-900 text-white px-4 py-3.5 sticky top-0 z-30">
          <button onClick={() => setDrawerAberto(true)} aria-label="Abrir menu">
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-semibold truncate">{title}</h1>
        </header>

        {/* Barra superior — desktop */}
        <header className="hidden lg:block bg-white border-b border-gray-200 px-8 py-5">
          <h1 className="text-lg font-bold text-gray-900">{title}</h1>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          {children}
        </main>
      </div>

      {editandoPerfil && sessao && (
        <Modal titulo="Meu perfil" onFechar={() => setEditandoPerfil(false)} maxWidth="max-w-md">
          <EditarUsuarioModal
            usuario={sessao}
            sessaoAdmin={sessao}
            onFechar={() => setEditandoPerfil(false)}
            onAtualizado={() => recarregarSessao()}
          />
        </Modal>
      )}
    </div>
  );
};

export default AdminLayout;
